import React, { PureComponent, Suspense } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Route, Switch, Redirect } from "react-router-dom";
import { Spin } from "antd";

import routesData from "./routes";
import agent from "./agent";
import storage from "./utils/storage";
import { logout, loggedIn, forceLogout } from "./store/actions/authActions";
import { appLoaded, updateCurrentUser } from "./store/actions/commonActions";
import { showErrorMessage } from "./utils/messages";
import "./App.less";

const Login = React.lazy(() => import("./views/Login"));
const Dashboard = React.lazy(() => import("./views/Dashboard"));

const styles = {
  appBootWrap: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(180deg, #f3faf7 0%, #ffffff 60%)",
  },
  appBootCard: {
    width: 360,
    maxWidth: "calc(100% - 32px)",
    padding: 18,
    borderRadius: 14,
    background: "linear-gradient(135deg, #ffffff 0%, #f6fffb 100%)",
    border: "1px solid rgba(27,107,79,0.14)",
    boxShadow: "0 12px 34px rgba(27,107,79,0.10)",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    alignItems: "center",
  },
  appBootTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#1b6b4f",
    letterSpacing: 0.2,
  },
  appBootSub: {
    fontSize: 12,
    color: "#6b8f80",
  },
};

class App extends PureComponent {
  componentDidMount() {
    const { appReady, goToLogin, loggedInUser, logoutUser } = this.props;

    const { token, expiry, issuedAt } = storage.getAccesTokenInfo();
    const isTokenExpired = storage.isTokenExpired(expiry, issuedAt);

    if (!token) {
      // no token => logged out
      goToLogin();
      appReady();
      return;
    }

    if (isTokenExpired) {
      agent.Auth.refreshToken(storage.getRefreshTokenInfo().token)
        .then((payload) => {
          storage.setAccesToken(payload.access_token, payload.exp);
          agent.setToken(payload.access_token);
          loggedInUser();
          appReady();
        })
        .catch((err) => {
          showErrorMessage(err);
          logoutUser();
          appReady();
          throw err;
        });

      return;
    }

    // valid token
    agent.setToken(token);
    loggedInUser();
    appReady();
  }

  render() {
    const {
      loggedIn: loggedInState,
      appLoaded: appLoadedState,
      logoutUser,
      appReady,
      updateUser,
    } = this.props;

    if (!appLoadedState) {
      return (
        <div style={styles.appBootWrap}>
          <div style={styles.appBootCard}>
            <Spin size="large" />
            <div style={styles.appBootTitle}>Loading Kickcash CMS…</div>
            <div style={styles.appBootSub}>Checking session & permissions</div>
          </div>
        </div>
      );
    }

    return (
      <Switch>
        {loggedInState ? (
          <Route
            path="/"
            render={() => (
              <Suspense
                fallback={(
                  <div style={styles.appBootWrap}>
                    <div style={styles.appBootCard}>
                      <Spin size="large" />
                      <div style={styles.appBootTitle}>Preparing dashboard…</div>
                      <div style={styles.appBootSub}>Almost there</div>
                    </div>
                  </div>
                )}
              >
                <Dashboard
                  logoutUser={logoutUser}
                  appReady={appReady}
                  updateUser={updateUser}
                  routesData={routesData}
                  paymentDashboard={false}
                />
              </Suspense>
            )}
          />
        ) : (
          <>
            <Route
              path="/"
              render={() => (
                <Suspense
                  fallback={(
                    <div style={styles.appBootWrap}>
                      <div style={styles.appBootCard}>
                        <Spin size="large" />
                        <div style={styles.appBootTitle}>Loading login…</div>
                        <div style={styles.appBootSub}>Please wait</div>
                      </div>
                    </div>
                  )}
                >
                  <Login />
                </Suspense>
              )}
            />
            {/* Optional: if you have separate /login route later, keep this */}
            <Redirect to="/" />
          </>
        )}
      </Switch>
    );
  }
}

App.propTypes = {
  loggedIn: PropTypes.bool.isRequired,
  appLoaded: PropTypes.bool.isRequired,
  appReady: PropTypes.func.isRequired,
  goToLogin: PropTypes.func.isRequired,
  loggedInUser: PropTypes.func.isRequired,
  updateUser: PropTypes.func.isRequired,
  logoutUser: PropTypes.func.isRequired,
};

const mapStateToProps = (state, ownProps) => ({
  appLoaded: state.common.appLoaded,
  loggedIn: state.auth.loggedIn,
  ...ownProps,
});

const mapDispatchToProps = (dispatch) => ({
  appReady: (user) => dispatch(appLoaded(user)),
  updateUser: (user) => dispatch(updateCurrentUser(user)),
  goToLogin: () => dispatch(logout()),
  loggedInUser: () => dispatch(loggedIn()),
  logoutUser: () => dispatch(forceLogout()),
});

export default connect(mapStateToProps, mapDispatchToProps)(App);
